/**
 * Add / edit provider document dialog
 */
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

export interface ProviderDocumentDialogValues {
  name: string;
  file: File | null;
}

interface ProviderDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: ProviderDocumentDialogValues) => void;
  isSaving?: boolean;
  editing?: boolean;
  initialName?: string;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ProviderDocumentDialog = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  editing = false,
  initialName = '',
}: ProviderDocumentDialogProps) => {
  const [name, setName] = useState(initialName);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setFile(null);
      setDragOver(false);
    }
  }, [open, initialName]);

  const handleFile = (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    if (!name.trim()) {
      const baseName = selected.name.replace(/\.[^/.]+$/, '');
      setName(baseName);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const handleSave = () => {
    onSave({ name: name.trim(), file });
  };

  const canSave = Boolean(name.trim()) && (editing || file);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          >
            <DescriptionIcon />
          </Box>
          <Box>
            <Typography variant="h6" lineHeight={1.2}>
              {editing ? 'Edit document' : 'Add document'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {editing ? 'Update the name or replace the file' : 'Upload a certificate, license, or other file'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          label="Document name"
          placeholder="e.g. Medical license, ID card"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3 }}
          autoFocus
        />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          File
        </Typography>

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: (theme) =>
              dragOver ? alpha(theme.palette.primary.main, 0.06) : 'action.hover',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="body2" fontWeight={600}>
            {editing ? 'Drop a new file here or click to browse' : 'Drop file here or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            PDF, images, or documents up to 10MB
          </Typography>
          <input
            ref={inputRef}
            hidden
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
          />
        </Box>

        {file && (
          <Chip
            icon={<FileIcon />}
            label={`${file.name} (${formatFileSize(file.size)})`}
            onDelete={() => setFile(null)}
            sx={{ mt: 2 }}
            color="primary"
            variant="outlined"
          />
        )}

        {editing && !file && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Leave file empty to keep the current uploaded file.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving}>
          {isSaving ? 'Saving...' : editing ? 'Update document' : 'Add document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderDocumentDialog;
